import React, { useEffect, useState } from "react";
import { apiFetch } from "../../services/api";
import {
  formatUsd,
  isLlmQuotaExhausted,
  readLlmQuota,
  readTrialStatusFromStorage,
} from "../../utils/llmQuota";

function formatDaysLeft(days) {
  if (days == null || Number.isNaN(Number(days))) return null;
  const n = Math.max(0, Math.floor(Number(days)));
  if (n === 0) return "expired";
  if (n === 1) return "1 day left";
  return `${n} days left`;
}

/**
 * Compact chrome badge: trial days + monthly LLM $ allowance / remaining.
 */
export default function TrialWorkspaceBadge({ compact = false }) {
  const [trial, setTrial] = useState(() => readTrialStatusFromStorage());

  useEffect(() => {
    const onTrialUpdated = (event) => {
      setTrial(event?.detail || readTrialStatusFromStorage());
    };
    const onStorage = (event) => {
      if (event.key === "trialStatus") setTrial(readTrialStatusFromStorage());
    };
    window.addEventListener("trialStatusUpdated", onTrialUpdated);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("trialStatusUpdated", onTrialUpdated);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch("/api/auth/trial-status");
        if (!res?.ok || cancelled) return;
        const data = await res.json();
        if (!data || typeof data !== "object" || cancelled) return;
        localStorage.setItem("trialStatus", JSON.stringify(data));
        window.dispatchEvent(
          new CustomEvent("trialStatusUpdated", { detail: data })
        );
      } catch {
        /* keep cached */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!trial) return null;

  const isTrial = Boolean(trial.is_trial_user);
  const daysLabel = isTrial ? formatDaysLeft(trial.trial_days_remaining) : null;
  const llm = readLlmQuota(trial);
  const exhausted = isLlmQuotaExhausted(trial);

  if (!isTrial && !llm) return null;

  const allowanceLabel = llm ? formatUsd(llm.allowance) : null;
  const remainingLabel = llm ? formatUsd(llm.remaining) : null;
  const llmShort = exhausted
    ? "AI budget used up"
    : remainingLabel && allowanceLabel
      ? `${remainingLabel} / ${allowanceLabel}`
      : null;
  const llmLong = exhausted
    ? `AI budget used up (${allowanceLabel || ""}/mo)`
    : remainingLabel && allowanceLabel
      ? `AI budget ${remainingLabel} of ${allowanceLabel} left`
      : null;

  const parts = [];
  if (isTrial) {
    parts.push(daysLabel ? `Trial · ${daysLabel}` : "Trial workspace");
  }
  if (llmShort) {
    parts.push(compact ? llmShort : llmLong);
  }
  const label = parts.join(" · ");
  const titleParts = [];
  if (isTrial) {
    titleParts.push(
      daysLabel ? `Trial workspace · ${daysLabel}` : "Trial workspace"
    );
  }
  if (llm) {
    titleParts.push(
      exhausted
        ? `Monthly AI allowance ${allowanceLabel} is used up. AI features are paused until next month or an admin raises the limit.`
        : `Monthly AI allowance ${allowanceLabel}; ${remainingLabel} remaining` +
            (llm.enforce ? " (enforced)" : "")
    );
  }
  const title = titleParts.join(". ");

  const tone = exhausted
    ? {
        compact:
          "max-w-[14rem] truncate rounded-md border border-rose-300 bg-rose-50 px-1.5 py-0.5 text-[10px] font-medium leading-4 text-rose-900",
        full: "inline-flex max-w-[24rem] items-center truncate rounded-lg border border-rose-300 bg-rose-50 px-2.5 py-1 text-xs font-medium text-rose-900",
      }
    : isTrial
      ? {
          compact:
            "max-w-[14rem] truncate rounded-md border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium leading-4 text-amber-900",
          full: "inline-flex max-w-[22rem] items-center truncate rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-900",
        }
      : {
          compact:
            "max-w-[12rem] truncate rounded-md border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-medium leading-4 text-slate-700",
          full: "inline-flex max-w-[20rem] items-center truncate rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-700",
        };

  return (
    <span
      className={compact ? tone.compact : tone.full}
      title={title}
      data-testid="trial-workspace-badge"
      data-llm-quota-exhausted={exhausted ? "true" : undefined}
    >
      {label}
    </span>
  );
}
